import { Children, type ReactNode } from "react";
import { convertToKebabCase } from "./strings";

/**
 * Generates a data-testid from the direct text children of a component.
 *
 * Only extracts direct string children, ignoring nested React elements.
 * Converts text to kebab-case and optionally prepends a prefix.
 *
 * @param children - The React children to extract text from
 * @param prefix - Optional prefix to prepend (e.g., "tabs-trigger")
 * @returns The generated data-testid string, or prefix (or empty string if both are falsy)
 *
 * @example
 * // TabsTrigger with text "Open aanvragen"
 * <TabsTrigger>Open aanvragen</TabsTrigger>
 * createDataTestidFromChildren(children, "tabs-trigger")
 * // Returns: "tabs-trigger-open-aanvragen"
 *
 */
export function createDataTestidFromChildren(children: ReactNode, prefix?: string) {
    const textChildren = Children.toArray(children)
        .filter((child) => typeof child === "string")
        .join(" ")
        .trim();

    if (!textChildren) {
        return prefix ?? "";
    }

    return `${prefix ? `${prefix}-` : ""}${convertToKebabCase(textChildren)}`;
}
